import { useApi } from '@/api';
import { TableSkeleton } from '@/components';
import {
  ArrowRightIcon,
  EtherScanPictogram,
  Flex,
  FormattedNumber,
  PercentageValue,
  formatNumber,
  DataTable,
  Box,
  RelativeDateTime
} from '@blockanalitica/ui';
import { useMemo } from 'react';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

function formatParameter(parameter, value, percents, seconds) {
  if (percents.includes(parameter)) {
    return <PercentageValue value={value} />;
  } else if (seconds.includes(parameter)) {
    return formatNumber(value, { notation: 'standard' }) + ' sec';
  } else {
    return <FormattedNumber value={value} />;
  }
}

export default function EventsTable({
  ilk,
  group,
  pagination,
  sorting,
  filters
}) {
  const [data, error] = useApi({
    url: `/groups/${group}/ilks/${ilk}/parameters/events/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      ilk: ilk,
      parameter: filters.filtersApplied.parameter
    }
  });

  const percents: string[] = useMemo(() => {
    return ['LR', 'Stability Fee', 'Chop', 'Buf', 'Cusp', 'Chip', 'Cut'];
  }, []);
  const seconds: string[] = useMemo(() => {
    return ['DC IAM ttl', 'Tail', 'Step'];
  }, []);

  if (error) {
    throw error;
  }

  if (data) {
    const columns: TableColumn[] = [
      {
        header: 'Parameter',
        getCell: (row) => row.parameter,
        cellAlign: 'flex-start',
        headerAlign: 'flex-start'
      },
      {
        header: 'Value change',
        getCell: (row) => (
          <Flex
            flexDirection="row"
            gap="2xs"
            alignContent="center"
            alignItems="center">
            {formatParameter(
              row.parameter,
              row.previous_value,
              percents,
              seconds
            )}
            <Box color="muted">
              <ArrowRightIcon size="1" />
            </Box>
            {formatParameter(
              row.parameter,
              row.current_value,
              percents,
              seconds
            )}
          </Flex>
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Time',
        getCell: (row) => (
          <Flex gap={['xs', '3xs']} color="muted">
            <RelativeDateTime value={row.datetime} />
            <a
              href={`https://etherscan.io/tx/${row.tx_hash}`}
              target="_blank"
              rel="noreferrer">
              <EtherScanPictogram size="0" />
            </a>
          </Flex>
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ];

    return (
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.block_number + row.parameter}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    );
  }
  return <TableSkeleton />;
}
